import { IAppState } from 'src/app/store/state/app.state';
import { Store, select } from '@ngrx/store';
import { Injectable } from '@angular/core';
import * as generalSelectors from '../selectors/general-selection.selectors';
import {combineLatest, Observable, of, switchMap} from 'rxjs';
import {filter, map} from "rxjs/operators";
import {IPortfolio} from "../../../models/portfolios";
import {ClientEntity} from "../../../models/clients";
import {deduplicatingSwitchMap} from "../../../utils/deduplicatingSwitchMap";
import {IClient} from "../../client/reducers/client.reducers";

export interface GeneralState {
    selectedEntities: ClientEntity[];
    selectedPortfolios: IPortfolio[];
    selectedClient: IClient;
}

@Injectable()
export class GeneralSelectionFacade {

    constructor(private store: Store<IAppState>) {
    }

    public getSelectedDate() {
        return this.store.pipe(select(generalSelectors.selectGeneralSelectionDate));
    }

    public getSelectedInvestorEntities() {
        return this.store.pipe(select(generalSelectors.selectGeneralSelectionSelectedEntities));
    }

    /**
     * Gets selected portfolios from the state when:
     * 1) the entity selector deals with portfolios (rather than entities)
     * 2) there are multiple portfolios
     * 
     * If the entity selector deals with portfolios BUT there is only one portfolio then this *appears* to stay empty.
     */
    public getSelectedPortfolios() {
      return this.store.pipe(select(generalSelectors.selectGeneralSelectionSelectedPortfolios));
    }

    public getClientTitle() {
        return this.store.pipe(select(generalSelectors.selectGeneralSelectionClientTitle));
    }

    public getSelectedClient() {
        return this.store.pipe(select(generalSelectors.selectGeneralSelectionSelectedClient));
    }

    public getSelectedCurrency() {
        return this.store.pipe(select(generalSelectors.selectGeneralSelectionSelectedCurrency));
    }

    public getState() {
        return this.store.pipe(select(generalSelectors.getGeneralSelectionState));
    }

    /**
     * This return returns an observable containg the values affected when choosing a new entity or client. This includes:
     * - Selected Entities
     * - Selected Portfolios
     * - Selected Clients
     * 
     * It is highly recommended to use this to ensure that the value you are using is properly in sync with the rest of
     * the state. It also performs some common null checks on the values. 
     */
    public getSelectedEntityPortfolioState(): Observable<GeneralState> {
        return combineLatest([
            this.getSelectedInvestorEntities(),
            this.getSelectedPortfolios(),
            this.getSelectedClient(),
        ]).pipe(
            filter(([selectedEntities, selectedPortfolios, selectedClient]) => 
                this.isEntitiesPopulated(selectedEntities) &&

                this.isPortfoliosPopulated(selectedPortfolios) &&

                this.portfoliosMatchEntities(selectedEntities, selectedPortfolios) &&
                
                this.portfoliosMatchClient(selectedClient, selectedPortfolios)
            ),
            deduplicatingSwitchMap(([selectedEntities, selectedPortfolios, selectedClient]) => {
                return of({selectedEntities, selectedPortfolios, selectedClient });
            })
        );
    }

    /**
     * Indicates if the selected portfolios correspond to the selected entities.
     */
    private portfoliosMatchEntities(selectedEntities: ClientEntity[], selectedPortfolios: IPortfolio[]): boolean {
        return selectedPortfolios
                .every(portfolio =>
                    selectedEntities.find(entity => entity.EntityId === portfolio.entityId))
            && selectedEntities.every(entity => selectedPortfolios.find(portfolio => entity.EntityId === portfolio.entityId));
    }
    
    private portfoliosMatchClient(selectedClient: IClient, selectedPortfolios: IPortfolio[]): boolean {
        if (!selectedClient || (selectedClient && selectedClient.Members.length <= 0))
            return true;
        
        return selectedPortfolios
            .every(portfolio =>
                selectedClient.Members.find(entity => entity.EntityId === portfolio.entityId))
    }

    /**
     * Indicates if the given array of portfolios is populated.
     */
    private isPortfoliosPopulated(portfolios: IPortfolio[]) : boolean {
        return !!portfolios && portfolios.length > 0;
    }

    /**
     * Indicates if the given array of entities is populated.
     */
    private isEntitiesPopulated(selectedEntities: ClientEntity[]) : boolean {
        return !!selectedEntities && selectedEntities.length > 0;
    }
}
